/**
 * PortalTimeline.jsx — generic, PII-free progress feed for the customer
 * portal. Events come pre-sanitised from the backend (no worker names).
 */
import React from 'react';
import {
  CheckCircle2, Clock, MapPin, Calendar, Image as ImageIcon,
  PauseCircle, Flag, Hammer, Truck,
} from 'lucide-react';

const EVENT_META = {
  created:       { icon: Flag,         color: 'text-slate-500' },
  scheduled:     { icon: Calendar,     color: 'text-blue-500' },
  en_route:      { icon: Truck,        color: 'text-amber-500' },
  arrived:       { icon: MapPin,       color: 'text-emerald-600' },
  work_started:  { icon: Hammer,       color: 'text-blue-600' },
  session_ended: { icon: Clock,        color: 'text-slate-500' },
  paused:        { icon: PauseCircle,  color: 'text-amber-500' },
  photos_added:  { icon: ImageIcon,    color: 'text-violet-500' },
  completed:     { icon: CheckCircle2, color: 'text-green-600' },
};

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const PortalTimeline = ({ events = [] }) => {
  if (!events.length) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center" data-testid="portal-timeline-empty">
        Updates will appear here as work progresses.
      </p>
    );
  }
  return (
    <ol className="relative space-y-0" data-testid="portal-timeline">
      {events.map((e, i) => {
        const meta = EVENT_META[e.type] || { icon: Clock, color: 'text-slate-500' };
        const Icon = meta.icon;
        const last = i === events.length - 1;
        return (
          <li key={`${e.type}-${e.at}-${i}`} className="flex gap-3" data-testid={`portal-timeline-event-${i}`}>
            <div className="flex flex-col items-center">
              <div className={`mt-0.5 rounded-full bg-white dark:bg-slate-900 border p-1.5 ${meta.color}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              {!last && <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 my-1" />}
            </div>
            <div className={`pb-4 ${last ? 'pb-0' : ''}`}>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{e.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmt(e.at)}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
